/**
 * 支払い済み申請(旧: 経費申請(承認不要))= 事後報告(2026-08-26 会議・spec v1.2 §3-D)。
 * 支払い済みの経費(例: 社用ガソリンカードでの購入)をレシート添付で報告する独立フロー。
 * - 全権限が使用可。フォームは経費申請と同一構成だが「予定」の文言なし(支払い金額・支払い日)
 * - 支払い者は「自分」固定(グレー表示)。**報告時に実績計上**
 * - 報告 → 経理確認(専用ページ)→ 承認者確認(④完了確認・完了でロック)の3ステップ(事前承認なし)
 * - 確認状況は申請一覧・経理確認・完了確認の各画面で確認する(2026-08-27 にこの画面の一覧は廃止)
 */
import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '@core/auth/AuthProvider';
import { useMasters } from '@core/masters/MastersProvider';
import { useToast } from '@core/components/Toast';
import { todayStr } from '@core/lib/date';
import { parseAmount } from '@core/lib/money';
import { uploadFiles } from '@core/expense/attachments';
import FileDropZone from '@core/expense/FileDropZone';
import SuggestInput from '@core/components/SuggestInput';
import DateField from '@core/components/DateField';
import { childrenOf, fetchHierarchyNodes } from '@core/masters/hierarchyRepo';
import { fetchCashAccounts } from '@core/cash/cashRepo';
import { computeDebitDate, debitRuleLabel, hasDebitRule } from '@core/lib/cardDebit';
import { createExpenseReport, fetchMyApplications, newApplicationId } from './applicationRepo';
import { reportFlowResult } from './flowResult';
import {
  PAYMENT_METHOD_LABELS,
  type DepartmentId,
  type HierarchyNode,
  type PaymentMethod,
  type CashAccount,
} from '@core/lib/types';

/** 報告で選べる支払い方法(2026-09-04 指示: クレカ・立替)。立替は経理の精算待ちに入る */
const REPORT_METHODS: PaymentMethod[] = ['card', 'reimbursement'];

function formatAmountInput(raw: string): string {
  const digits = raw
    .replace(/[０-９]/g, (c) => String.fromCharCode(c.charCodeAt(0) - 0xfee0))
    .replace(/\D/g, '');
  if (digits === '') return '';
  return Number(digits).toLocaleString('ja-JP');
}

export default function ReportPage() {
  const { user } = useAuth();
  const { companies, departments, accounts, subAccountsOf, fiscal, defaultCompanyId, companyName } = useMasters();
  const toast = useToast();

  const defaultDept = (user?.primary_department_id ?? 'honbu') as DepartmentId;
  const [companyId, setCompanyId] = useState(defaultCompanyId);
  const [departmentId, setDepartmentId] = useState<DepartmentId>(defaultDept);
  const [nodes, setNodes] = useState<HierarchyNode[]>([]);
  const [locationId, setLocationId] = useState('');
  const [accountId, setAccountId] = useState('');
  const [subAccountId, setSubAccountId] = useState('');
  const [postingDate, setPostingDate] = useState(todayStr());
  const [paymentDate, setPaymentDate] = useState(todayStr());
  const [amountText, setAmountText] = useState('');
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('card');
  const [counterparty, setCounterparty] = useState('');
  const [counterpartyOptions, setCounterpartyOptions] = useState<string[]>([]);
  const [description, setDescription] = useState('');
  const [note, setNote] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [busy, setBusy] = useState(false);
  /** クレカ(2026-09-07): 使った会社のカード。締め日から引き落とし日を自動で出し、CFへ出金(予定)を記録する */
  const [cards, setCards] = useState<CashAccount[]>([]);
  const [cardId, setCardId] = useState('');
  useEffect(() => {
    setCardId('');
  }, [companyId]);
  useEffect(() => {
    fetchCashAccounts()
      .then((list) => setCards(list.filter((a) => a.active && a.kind === 'card')))
      .catch(() => setCards([]));
  }, []);
  const isCard = paymentMethod === 'card';
  // 選んだ会社のカードだけを出す(同じ名前のカードが会社ごとにあるため)。会社「その他」は全社分
  const companyCards = cards.filter((c) => companyId === 'sonota' || c.company_id === companyId);
  const card = cards.find((c) => c.id === cardId) ?? null;
  const debitDate = isCard && card ? computeDebitDate(paymentDate, card) : null;

  // 支払先の入力候補(自分の過去申請から。経費申請と同じ・2026-08-27)
  useEffect(() => {
    if (!user) return;
    fetchMyApplications(user.id)
      .then((list) => {
        const names = [...new Set(list.map((t) => t.counterparty).filter((c): c is string => !!c?.trim()))];
        setCounterpartyOptions(names.sort((a, b) => a.localeCompare(b, 'ja')));
      })
      .catch(() => setCounterpartyOptions([]));
  }, [user]);

  const expenseAccounts = useMemo(
    () =>
      accounts
        .filter(
          (a) =>
            a.active &&
            (a.department_ids?.length ?? 0) > 0 &&
            (departmentId === 'sonota' || a.department_ids!.includes(departmentId)),
        )
        .sort((a, b) => a.display_order - b.display_order),
    [accounts, departmentId],
  );
  useEffect(() => {
    if (accountId && !expenseAccounts.some((a) => a.id === accountId)) {
      setAccountId('');
      setSubAccountId('');
    }
  }, [expenseAccounts, accountId]);

  useEffect(() => {
    fetchHierarchyNodes(departmentId)
      .then(setNodes)
      .catch(() => setNodes([]));
    setLocationId('');
  }, [departmentId]);

  const locationOptions = useMemo(() => {
    const sites = childrenOf(nodes, null);
    return sites.flatMap((site) => [
      { id: site.id, label: site.name },
      ...childrenOf(nodes, site.id).map((pl) => ({ id: pl.id, label: `${site.name}/${pl.name}` })),
    ]);
  }, [nodes]);

  async function submit() {
    if (!user) return;
    const amount = parseAmount(amountText);
    if (amount === null) {
      toast('支払い金額を正しく入力してください');
      return;
    }
    if (!accountId) {
      toast('科目①を選んでください');
      return;
    }
    if (!counterparty.trim()) {
      toast('支払先を入力してください');
      return;
    }
    if (!description.trim()) {
      toast('摘要は必須です');
      return;
    }
    if (files.length === 0) {
      toast('レシート(領収書)の添付が必須です');
      return;
    }
    if (isCard && !cardId) {
      toast('使ったカードを選んでください(マスタ管理→支払い方法に登録されたクレカ)');
      return;
    }
    setBusy(true);
    try {
      const id = newApplicationId();
      const receipts = await uploadFiles(id, 'receipts', files, user.id);
      const { code, warning } = await createExpenseReport({
        id,
        input: {
          companyId,
          departmentId,
          hierarchyNodeId: locationId,
          accountId,
          subAccountId,
          // クレカ: 計上日=購入日、支払い日(CF)=引き落とし日(設定が無ければ購入日のまま)
          postingDate: isCard ? paymentDate : postingDate,
          paymentDate: isCard ? (debitDate ?? paymentDate) : paymentDate,
          amount,
          paymentMethod,
          counterparty,
          description,
          note,
          paymentAccountId: isCard ? cardId : null,
          purchaseDate: isCard ? paymentDate : null,
        },
        receipts,
        applicant: user,
        fiscalStartMonth: fiscal.start_month,
      });
      reportFlowResult(toast, `${code} を報告しました(実績に計上済み)。経理確認→承認者確認に進みます`, { warning });
      setAmountText('');
      setCounterparty('');
      setDescription('');
      setNote('');
      setFiles([]);
    } catch (e) {
      toast(e instanceof Error ? e.message : '報告に失敗しました');
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="screen" style={{ maxWidth: 1100 }}>
      <div className="card" style={{ maxWidth: 760 }}>
        <h3 style={{ display: 'flex', alignItems: 'center' }}>
          支払い済みの経費を報告する
          {/* 入力のオールクリア(2026-08-28。控えめな配置・確認あり) */}
          <button
            className="txn-link"
            style={{ marginLeft: 'auto', fontSize: 12, fontWeight: 400, color: 'var(--muted)' }}
            onClick={() => {
              if (!window.confirm('入力内容をすべてクリアしますか?')) return;
              setCompanyId(defaultCompanyId);
              setDepartmentId(defaultDept);
              setLocationId('');
              setAccountId('');
              setSubAccountId('');
              setPostingDate(todayStr());
              setPaymentDate(todayStr());
              setAmountText('');
              setPaymentMethod('card');
              setCounterparty('');
              setDescription('');
              setNote('');
              setFiles([]);
              toast('入力内容をクリアしました');
            }}
          >
            入力をクリア
          </button>
        </h3>
        {/* 上段の説明文は削除(2026-08-31 指示) */}
        {/* 経費申請と同じ項目構成・並び(2026-08-27 指摘対応)。
            変更できない欄(申請者・支払い者=自分)は残してグレーアウトする */}
        <div className="form-grid" style={{ gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', marginTop: 10 }}>
          <div>
            <label>申請者</label>
            <input readOnly value={user?.display_name ?? ''} style={{ background: '#EEF2F8', color: 'var(--muted)' }} />
          </div>
          <div>
            <label>
              会社<span className="req">必須</span>
            </label>
            <select value={companyId} onChange={(e) => setCompanyId(e.target.value)}>
              {companies.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label>
              事業部<span className="req">必須</span>
            </label>
            <select value={departmentId} onChange={(e) => setDepartmentId(e.target.value as DepartmentId)}>
              {departments.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label>拠点/場所(任意)</label>
            <select value={locationId} onChange={(e) => setLocationId(e.target.value)}>
              <option value="">—</option>
              {locationOptions.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label>
              科目①<span className="req">必須</span>
            </label>
            <select
              value={accountId}
              onChange={(e) => {
                setAccountId(e.target.value);
                setSubAccountId('');
              }}
            >
              <option value="">選択してください</option>
              {expenseAccounts.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label>科目②(任意)</label>
            <select value={subAccountId} disabled={!accountId} onChange={(e) => setSubAccountId(e.target.value)}>
              <option value="">—</option>
              {(accountId ? subAccountsOf(accountId) : []).map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label>
              計上日<span className="req">必須</span>
            </label>
            {isCard ? (
              <input value={`${paymentDate.replace(/-/g, '/')}(購入日と同じ)`} readOnly disabled style={{ background: '#EEF2F8', color: 'var(--ink)' }} />
            ) : (
              <DateField value={postingDate} onChange={setPostingDate} />
            )}
          </div>
          <div>
            <label>
              支払い金額(税込み)<span className="req">必須</span>
            </label>
            <input
              inputMode="numeric"
              value={amountText}
              onChange={(e) => setAmountText(formatAmountInput(e.target.value))}
              placeholder="例)5,500"
            />
          </div>
          <div>
            <label>
              {isCard ? '購入日(＝計上日)' : '支払い日'}<span className="req">必須</span>
            </label>
            <DateField value={paymentDate} onChange={setPaymentDate} />
          </div>
          <div>
            <label>
              支払い方法<span className="req">必須</span>
            </label>
            <select value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value as PaymentMethod)}>
              {REPORT_METHODS.map((m) => (
                <option key={m} value={m}>
                  {PAYMENT_METHOD_LABELS[m]}
                </option>
              ))}
            </select>
          </div>
          {isCard && (
            <>
              <div>
                <label>
                  使ったカード<span className="req">必須</span>
                </label>
                <select value={cardId} onChange={(e) => setCardId(e.target.value)}>
                  <option value="">選択してください</option>
                  {companyCards.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                      {companyId === 'sonota' ? `(${companyName(c.company_id)})` : ''}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label>引き落とし日(自動)</label>
                <input
                  value={debitDate ? debitDate.replace(/-/g, '/') : ''}
                  readOnly
                  disabled
                  placeholder={card && !hasDebitRule(card) ? 'カードの締め日・引き落とし日が未設定' : 'カードを選ぶと表示'}
                  style={{ background: '#EEF2F8', color: 'var(--ink)' }}
                />
                {card && (
                  <div className="hint" style={{ marginTop: 3 }}>
                    {hasDebitRule(card) ? `${card.name}: ${debitRuleLabel(card)}` : 'マスタ管理→支払い方法で、このカードの締め日・引き落とし日を設定してください'}
                  </div>
                )}
              </div>
            </>
          )}
          <div>
            <label>支払い者</label>
            <select value="self" disabled style={{ background: '#EEF2F8', color: 'var(--muted)' }}>
              <option value="self">自分</option>
            </select>
          </div>
          <div className="fw">
            <label>
              レシート(領収書)<span className="req">必須</span>
            </label>
            <FileDropZone files={files} onChange={setFiles} />
          </div>
          {/* 支払先(2026-08-27 追加): 経費申請と同じ自由記述+部分一致の候補表示 */}
          <div className="fw">
            <label>
              支払先<span className="req">必須</span>
            </label>
            <SuggestInput
              value={counterparty}
              onChange={setCounterparty}
              options={counterpartyOptions}
              placeholder="例)〇〇石油"
            />
          </div>
          <div className="fw">
            <label>
              摘要(購入品明細、または受領書の場合は但し書き)<span className="req">必須</span>
            </label>
            <textarea
              value={description}
              rows={2}
              onChange={(e) => setDescription(e.target.value)}
              style={{ width: '100%', padding: '9px 11px', border: '1px solid var(--line)', borderRadius: 6, fontSize: 13.5, fontFamily: 'inherit' }}
              placeholder="例)ガソリン(社用車 品川300 あ 12-34)"
            />
          </div>
          <div className="fw">
            <label>備考(任意)</label>
            <textarea
              value={note}
              rows={2}
              onChange={(e) => setNote(e.target.value)}
              style={{ width: '100%', padding: '9px 11px', border: '1px solid var(--line)', borderRadius: 6, fontSize: 13.5, fontFamily: 'inherit' }}
            />
          </div>
        </div>
        <button className="btn-green" style={{ marginTop: 12 }} disabled={busy} onClick={() => void submit()}>
          {busy ? '処理中…' : '報告する(実績計上)'}
        </button>
        <p className="hint" style={{ marginTop: 10 }}>
          報告 → 経理確認(経理)→ 承認者確認(承認者・完了)の順に進みます。
          進捗と確認状況は「申請一覧」から確認できます(状態: 経理確認待ち → 承認者確認待ち → 完了)。
        </p>
      </div>
    </section>
  );
}
